import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import { GlobalContext } from '../../../../../store';
import { ScoreType } from '../../../../../store/admin/eventFormats/store';
import { IMetaFormat } from '../../../../../store/admin/roundFormats/store';

import {
  IContestSelectorOption,
  ISetContestSelectorOptions,
  setContestSelectorOptions
} from './BoundActionCreators/setContestSelectorOptions';
import { ISetLeaderboards, setLeaderboards } from './BoundActionCreators/setLeaderboards';
import { IUpdateEnrollments, updateEnrollments } from './BoundActionCreators/updateEnrollments';
import { ISearch, search } from './BoundActionCreators/search';
import { ISelectFormat, selectFormat } from './BoundActionCreators/selectFormat';

export interface IOpenGroups {
  [groupId: number]: boolean;
}

export interface ILeaderboard {
  aggregation: number;
  enrollments: number[];
  flight: number;
  format: number;
  hasAwards: boolean;
  id: number;
  scoreType: ScoreType;
}

export interface ILeaderboards {
  [id: number]: ILeaderboard;
}

type IActions =
  | {
      type: 'setPlayerLookupValue';
      value: string;
    }
  | { type: 'selectFormat'; value: { scoreType: ScoreType; metaFormat: IMetaFormat } }
  | { type: 'setContestSelectorOptions'; value: IContestSelectorOption[] }
  | { type: 'updateLeaderboard'; value: ILeaderboard }
  | { type: 'setLeaderboards'; value: ILeaderboards };

interface IContestSetupState {
  playerLookupValue: string;
  selectedMetaFormat?: IMetaFormat;
  selectedScoreType?: ScoreType;
  contestSelectorOptions: IContestSelectorOption[];
  leaderboards: ILeaderboards;
  selectedLeaderboard?: ILeaderboard;
}

const initialState: IContestSetupState = {
  contestSelectorOptions: [],
  leaderboards: {},
  playerLookupValue: ''
};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const ContestSetupStateContext = createContext<IContestSetupState | undefined>(undefined);
const ContestSetupDispatchContext = createContext<IDispatch | undefined>(undefined);

const ContestSetupReducer = (state: IContestSetupState, action: IActions): IContestSetupState => {
  switch (action.type) {
    case 'setPlayerLookupValue': {
      return {
        ...state,
        playerLookupValue: action.value
      };
    }
    case 'selectFormat': {
      const { metaFormat, scoreType } = action.value;

      const selectedLeaderboard = Object.values(state.leaderboards).find(
        (leaderboard: ILeaderboard) =>
          metaFormat.format.prefix === 'ctp'
            ? leaderboard.format === metaFormat.id
            : leaderboard.scoreType === scoreType && leaderboard.format === metaFormat.id
      );

      return {
        ...state,
        selectedLeaderboard,
        selectedMetaFormat: metaFormat,
        selectedScoreType: scoreType
      };
    }
    case 'setContestSelectorOptions': {
      return {
        ...state,
        contestSelectorOptions: action.value
      };
    }
    case 'updateLeaderboard': {
      return {
        ...state,
        leaderboards: { ...state.leaderboards, [action.value.id]: action.value },
        selectedLeaderboard:
          state.selectedLeaderboard && action.value.id === state.selectedLeaderboard.id
            ? action.value
            : state.selectedLeaderboard
      };
    }
    case 'setLeaderboards': {
      return { ...state, leaderboards: action.value };
    }
  }
};

interface ContestSetupProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  search: ISearch;
  selectFormat: ISelectFormat;
  setContestSelectorOptions: ISetContestSelectorOptions;
  setLeaderboards: ISetLeaderboards;
  updateEnrollments: IUpdateEnrollments;
}

export type IStateDispatchActions<T> = (
  state: IContestSetupState,
  dispatch: (action: IActions) => void
) => T;

const ContestSetupProvider = ({ children }: ContestSetupProviderProps) => {
  const [state, dispatch] = useReducer(ContestSetupReducer, initialState as never);

  const actions: IBoundActionCreators = {
    search: search(state, dispatch),
    selectFormat: selectFormat(state, dispatch),
    setContestSelectorOptions: setContestSelectorOptions(state, dispatch),
    setLeaderboards: setLeaderboards(state, dispatch),
    updateEnrollments: updateEnrollments(state, dispatch)
  };

  const {} = useContext(GlobalContext);

  return (
    <ContestSetupStateContext.Provider value={state}>
      <ContestSetupDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </ContestSetupDispatchContext.Provider>
    </ContestSetupStateContext.Provider>
  );
};

const useContestSetupState = () => {
  const context = React.useContext(ContestSetupStateContext);
  if (context === undefined) {
    throw new Error('useContestSetupState must be used within a ContestSetupProvider');
  }
  return context;
};

const useContestSetupDispatch = () => {
  const context = React.useContext(ContestSetupDispatchContext);
  if (context === undefined) {
    throw new Error('useContestSetupDispatch must be used within a ContestSetupProvider');
  }
  return context;
};

export { ContestSetupProvider, useContestSetupState, useContestSetupDispatch };
